import { createFileRoute } from '@tanstack/react-router'
import { Check, Sparkles, X } from 'lucide-react'
import { useState } from 'react'

import { useDemoScenarios } from '@/api/queries'
import type { DemoScenario } from '@/api/types'
import {
  Badge,
  Card,
  CardHeader,
  ErrorState,
  Meter,
  RecordingDot,
  Skeleton,
  cn,
  complianceTone,
} from '@/components/ui/primitives'
import { apiErrorLabel } from '@/shared/labels'

export const Route = createFileRoute('/_auth/demo')({
  component: DemoPage,
})

/** Демо-данные приходят с бэкенда — те же, что у серверной панели и мобильного
 *  приложения, третьей копии текстов не появляется. */
function DemoPage() {
  const scenarios = useDemoScenarios()
  const [activeKey, setActiveKey] = useState<string | null>(null)

  if (scenarios.isError) {
    return (
      <Card>
        <ErrorState
          message={apiErrorLabel(
            (scenarios.error as { detail?: unknown }).detail,
            'Не удалось загрузить демо-сценарии',
          )}
          onRetry={() => void scenarios.refetch()}
        />
      </Card>
    )
  }

  if (!scenarios.data) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-[420px]" />
      </div>
    )
  }

  const current = scenarios.data.find((item) => item.key === activeKey) ?? scenarios.data[0] ?? null
  if (!current) return null

  return (
    <div className="space-y-5">
      <header>
        <h1 className="font-display text-xl font-bold text-ink">Демо</h1>
        <p className="mt-0.5 text-[13px] text-ink-subtle">
          Примеры разговоров для показов — данные подготовленные, не из базы.
        </p>
      </header>

      <div className="flex flex-wrap items-center gap-1 rounded-[var(--radius-control)] border border-line bg-surface p-0.5">
        {scenarios.data.map((scenario) => (
          <button
            key={scenario.key}
            type="button"
            onClick={() => setActiveKey(scenario.key)}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-[calc(var(--radius-control)-2px)] px-3 py-1.5 text-[13px] font-medium transition-colors',
              current.key === scenario.key
                ? 'bg-brand-800 text-white dark:bg-brand-600'
                : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
            )}
          >
            {scenario.is_live && <RecordingDot />}
            {scenario.tab_label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader
            title={current.title}
            description={current.date_label}
            actions={
              <div className="flex flex-wrap items-center gap-2">
                {current.is_live && (
                  <Badge tone="warning">
                    <RecordingDot />
                    Идёт запись
                  </Badge>
                )}
                <Badge
                  tone={
                    current.sentiment.cls === 'pos'
                      ? 'positive'
                      : current.sentiment.cls === 'neg'
                        ? 'negative'
                        : 'neutral'
                  }
                >
                  Тональность: {current.sentiment.label.toLowerCase()}
                </Badge>
                {current.risk_label && <Badge tone="negative">{current.risk_label}</Badge>}
              </div>
            }
          />
          <ul className="space-y-2 p-4">
            {current.dialog.map((line, index) => {
              const isSeller = line.speaker === 'seller' || line.speaker === 'agent'
              return (
                <li key={index} className={cn('flex', isSeller ? 'justify-end' : 'justify-start')}>
                  <div
                    className={cn(
                      'max-w-[85%] rounded-[var(--radius-card)] border px-3 py-2',
                      isSeller
                        ? 'border-brand-100 bg-brand-50 dark:border-brand-900 dark:bg-brand-950'
                        : 'border-line bg-surface-muted',
                    )}
                  >
                    <p className="mb-1 flex items-center gap-2">
                      <span className="text-[11px] font-semibold tracking-wide text-ink-muted uppercase">
                        {isSeller ? 'Продавец' : 'Клиент'}
                      </span>
                      <span className="font-mono text-[11px] text-ink-subtle">{line.time}</span>
                    </p>
                    <p className="text-[13px] leading-relaxed text-ink">{line.text}</p>
                  </div>
                </li>
              )
            })}
            {current.partial_text && (
              <li className="flex justify-start">
                <div className="max-w-[85%] rounded-[var(--radius-card)] border border-dashed border-line bg-surface-muted/60 px-3 py-2">
                  <p className="text-[13px] text-ink-subtle italic">
                    {current.partial_text}
                    <span className="ml-1 animate-pulse">▍</span>
                  </p>
                </div>
              </li>
            )}
          </ul>
        </Card>

        <DemoReview scenario={current} />
      </div>
    </div>
  )
}

function DemoReview({ scenario }: { scenario: DemoScenario }) {
  const tone = complianceTone(scenario.compliance.percent)
  return (
    <Card>
      <CardHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Sparkles className="size-4 text-advice" />
            AI-разбор разговора
          </span>
        }
      />
      <div className="space-y-5 p-5">
        <div className="flex items-center gap-4">
          <p
            className={cn(
              'font-display text-[40px] leading-none font-bold',
              tone === 'positive' && 'text-positive',
              tone === 'warning' && 'text-warning',
              tone === 'negative' && 'text-negative',
            )}
          >
            {scenario.compliance.percent}%
          </p>
          <div className="flex-1">
            <p className="mb-1.5 text-[13px] text-ink-muted">
              соблюдение скрипта{scenario.is_live ? ' (пока)' : ''}
            </p>
            <Meter value={scenario.compliance.percent} tone={tone} />
          </div>
        </div>

        <ul className="space-y-2">
          {scenario.steps.map((step, index) => (
            <li key={index} className="flex gap-2.5">
              <span
                className={cn(
                  'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full',
                  step.done ? 'bg-positive-soft text-positive' : 'bg-negative-soft text-negative',
                )}
                aria-hidden="true"
              >
                {step.done ? <Check className="size-3" /> : <X className="size-3" />}
              </span>
              <div className="min-w-0">
                <p className="text-[13px] text-ink">{step.title}</p>
                {step.comment && <p className="mt-0.5 text-xs text-negative">{step.comment}</p>}
              </div>
            </li>
          ))}
        </ul>

        {scenario.recommendations.length > 0 && (
          <div
            className={cn(
              'rounded-[var(--radius-control)] border-l-4 px-4 py-3',
              scenario.reco_urgent
                ? 'border-l-accent-500 bg-negative-soft'
                : 'border-l-advice bg-advice-soft',
            )}
          >
            <h3
              className={cn(
                'mb-1.5 text-[13px] font-semibold',
                scenario.reco_urgent ? 'text-negative' : 'text-advice',
              )}
            >
              {scenario.reco_heading}
            </h3>
            <ul className="list-inside list-disc space-y-1 text-[13px] text-ink">
              {scenario.recommendations.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
        )}

        {scenario.summary && (
          <p className="border-t border-line pt-4 text-[13px] leading-relaxed text-ink-muted italic">
            {scenario.summary}
          </p>
        )}
      </div>
    </Card>
  )
}
